import React, { useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useIntl } from 'react-intl';
import { Utils } from 'jamespot-react-components';
import { Avatar } from './Avatar';
import { jObjectLittle } from 'jamespot-user-api';
import { commonComponentsRegistry } from '../registry/CommonRegistry';

const JRCFlexBox = commonComponentsRegistry.getLazyComponent('FlexBox');
const JRCModalContent = commonComponentsRegistry.getLazyComponent('ModalContent');
const JRCModalLayout = commonComponentsRegistry.getLazyComponent('ModalLayout');
const JRCStyledHref = commonComponentsRegistry.getLazyComponent('StyledHref');
const JRCTooltip = commonComponentsRegistry.getLazyComponent('Tooltip');
const JRCTypography = commonComponentsRegistry.getLazyComponent('Typography');

export type AudienceProps<T extends jObjectLittle> = {
    audience: T[];
    limit?: number;
};

const LIMIT = 2;

const AudienceWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 24px;
`;

export const AvatarLink = styled.a<{ overlap?: boolean }>`
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    width: 40px;
    height: 40px;
    border-radius: 40px;
    overflow: hidden;
    color: ${(props) => props.theme.color.white};
    background-color: ${(props) => props.theme.color.lavender};
    border: 4px solid ${(props) => props.theme.color.white};
    margin-left: ${(props) => (props.overlap ? '-18px' : '0')};
    cursor: pointer;
`;

export const Audience = <T extends jObjectLittle>({ audience, limit = LIMIT }: AudienceProps<T>) => {
    const intl = useIntl();
    const theme = useTheme();
    const [isModalOpen, setModalOpen] = useState(false);

    return (
        <>
            <JRCFlexBox>
                {[...audience].splice(0, limit).map((item, index) => (
                    <AvatarLink overlap={index !== 0} href={item._url} target={'_blank'} key={item.id}>
                        <JRCTooltip description={item.title === undefined ? ' ' : item.title} position="left">
                            <Avatar
                                uri={item.uri}
                                size={4}
                                variant="circle"
                                alt="image"
                                timestamp={item.dateModified ? Utils.date.strToTimestamp(item.dateModified) : undefined}
                                focusable
                            />
                        </JRCTooltip>
                    </AvatarLink>
                ))}
                {audience.length > limit && (
                    <AvatarLink onClick={() => setModalOpen(true)} overlap={true}>
                        +{audience.length - limit}
                    </AvatarLink>
                )}
            </JRCFlexBox>
            {audience.length > limit && (
                <JRCModalLayout
                    closeHandler={() => setModalOpen(false)}
                    enableClickAwayCloseModal
                    open={isModalOpen}
                    variant={'fixed-height'}
                    maxHeight={'800px'}>
                    <JRCModalContent
                        title={intl.formatMessage({ id: 'GLOBAL_Audience' })}
                        overflow={'scroll'}
                        onClickIconClose={() => setModalOpen(false)}>
                        <AudienceWrapper>
                            {audience.map((item, i) => (
                                <JRCFlexBox key={`audience-${i}`} y={'center'} gap={theme.space.l}>
                                    <Avatar size={5} uri={`${item.type}/${item.id}`} />
                                    <JRCStyledHref href={`${item.type}/${item.id}`}>
                                        <JRCTypography>{item.title}</JRCTypography>
                                    </JRCStyledHref>
                                </JRCFlexBox>
                            ))}
                        </AudienceWrapper>
                    </JRCModalContent>
                </JRCModalLayout>
            )}
        </>
    );
};
